import { gettext } from '@eventbrite/i18n';
import { Button } from '@eventbrite/marmalade';
import React, { useState } from 'react';
import { useEventBasicInformationContext } from '../../../../../contexts';
import type { LocalizedDateTime } from '../../../../../types';
import { getFormattedDateWithTz } from '../../../../utils/localizedDates';
import { ShowMoreButton, ShowMoreContent } from '../ShowMore/ShowMore';
import styles from './CondensedConversionBar.module.scss';

type CondensedConversionBarProps = {
    startDateTime?: LocalizedDateTime;
    priceTag: string;
    checkoutButton: React.ReactNode;
    isOnline?: boolean;
    locale: string;
    externalTickets?: {
        url: string;
        text: string;
        available: boolean;
        shouldShowExternalTicketingProvider: boolean;
        externalTicketingProviderLabelText?: string;
    };
    extraPriceInformation?: string;
};

const formatDate = (
    date: LocalizedDateTime,
    locale: string,
    isOnline: boolean,
) => {
    return getFormattedDateWithTz({
        date,
        formatString: 'MMM d',
        locale: locale,
        isOnlineEvent: isOnline,
    });
};

const formatTime = (
    date: LocalizedDateTime,
    locale: string,
    isOnline: boolean,
) => {
    return getFormattedDateWithTz({
        date,
        formatString: 'p zzz',
        locale: locale,
        isOnlineEvent: isOnline,
    });
};

export const CondensedConversionBar: React.FC<CondensedConversionBarProps> = ({
    startDateTime,
    priceTag,
    checkoutButton,
    isOnline = false,
    locale,
    externalTickets,
    extraPriceInformation,
}) => {
    const { hideStartDate } = useEventBasicInformationContext();
    const [showMoreOpen, setShowMoreOpen] = useState(false);

    const ExternalTicketsButton = () => (
        <Button variant="primary" size="block">
            <a
                href={externalTickets?.url}
                rel="noreferrer nofollow"
                target="_blank"
                className={styles.externalTicketsLink}
            >
                {externalTickets?.text}
            </a>
        </Button>
    );

    return (
        <div className={styles.wrapper} data-testid="condensed-conversion-bar">
            <div className={styles.topSection}>
                <div className={styles.dateAndPriceWrapper}>
                    <div className={styles.priceTagWrapper}>
                        <span className={styles.priceTag}>
                            {priceTag}
                            {externalTickets?.shouldShowExternalTicketingProvider && (
                                <>
                                    {` · ${externalTickets?.externalTicketingProviderLabelText}`}
                                </>
                            )}
                        </span>
                        {extraPriceInformation && (
                            <ShowMoreButton
                                onToggle={() => setShowMoreOpen(!showMoreOpen)}
                                variant="condensed"
                            />
                        )}
                    </div>
                    {!hideStartDate && startDateTime && (
                        <span>
                            {gettext('%(date)s · %(time)s', {
                                date: formatDate(
                                    startDateTime,
                                    locale,
                                    isOnline,
                                ),
                                time: formatTime(
                                    startDateTime,
                                    locale,
                                    isOnline,
                                ),
                            })}
                        </span>
                    )}
                </div>
                <div className={styles.checkoutButtonWrapper}>
                    {externalTickets?.available ? (
                        <ExternalTicketsButton />
                    ) : (
                        checkoutButton
                    )}
                </div>
            </div>
            {extraPriceInformation && (
                <ShowMoreContent
                    isOpen={showMoreOpen}
                    content={extraPriceInformation}
                    variant="condensed"
                />
            )}
        </div>
    );
};
